// External Libraries
import * as React from "react";
import { observer } from "mobx-react";

// Types
import { Hotel } from "typings/microserviceModels/hotels-flex-module";

// UITK
import { UitkCardLink, UitkCard, UitkCardContentSection } from "uitk-react-cards";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from "uitk-react-images";
import { classNames } from "uitk-react-utils";

// Components
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { useHotelsContext } from "components/flexComponents/Hotels/components/HotelsContext";
import {
  SummarizedContent,
  SummarizedContentViewProps,
} from "components/flexComponents/Hotels/components/SummarizedHotelCard/SummarizedContent";

// Utils
import { getOptimizedImageUrl } from "src/components/flexComponents/Hotels/utils/hotelOptimizedImage";
import { useSendEgRecsClickstreamSelectedEvent } from "components/flexComponents/Hotels/hooks/useSendEgRecsClickstreamSelectedEvent";

interface SummarizedHotelBaseProps {
  hotel: Hotel;
  isMobile: boolean;
}
interface SummarizedHotelCardProps extends SummarizedHotelBaseProps, SummarizedContentViewProps {
  index: number;
  className?: string;
  imageAspectRatio?: UitkFigureAspectRatioType;
  isTopImage?: boolean;
  callToActionStyle?: string;
}

export const SummarizedHotelCardComponent: React.FC<SummarizedHotelCardProps> = ({
  hotel,
  isMobile,
  index,
  className,
  imageAspectRatio,
  isTopImage,
  callToActionStyle,
  ...contentViewProps
}) => {
  const { templateComponent, context } = useHotelsContext();
  const hotelImageUrl = getOptimizedImageUrl(hotel, context);
  const { hotelName, hotelInfositeUrl } = hotel;
  const moduleName = templateComponent.type as string;
  const imageColSpan = isTopImage ? 6 : 2;
  const contentColSpan = isTopImage ? 6 : 4;

  const getRatio = () => {
    if (imageAspectRatio) {
      return imageAspectRatio;
    }
    if (isTopImage) {
      return UitkFigureAspectRatioType.R21_9;
    }
    if (isMobile) {
      return UitkFigureAspectRatioType.R1_1;
    }
    return UitkFigureAspectRatioType.R4_3;
  };

  const contentProps = {
    hotel,
    ...contentViewProps,
  };

  const hasFullCardLink = callToActionStyle === "fullCard";
  const linkId = `hotelcard-link-${hasFullCardLink ? "full-card-" : ""}${index}`;

  const link = (
    <div onClick={useSendEgRecsClickstreamSelectedEvent(hotel, index)}>
      <UitkCardLink>
        <TrackedLink data-testid={linkId} moduleName={moduleName} rfrr="card" href={hotelInfositeUrl}>
          {hotelName}
        </TrackedLink>
      </UitkCardLink>
    </div>
  );

  return (
    <UitkCard className={classNames("SummarizedHotelCard", className)}>
      <UitkLayoutGrid columns={6} data-testid="summarized-hotelcard">
        <UitkLayoutGridItem colSpan={imageColSpan}>
          <UitkFigure ratio={getRatio()}>
            <UitkImage src={hotelImageUrl} alt={hotelName} placeholderImage lazyLoading="lazy" />
          </UitkFigure>
        </UitkLayoutGridItem>
        <UitkLayoutGridItem colSpan={contentColSpan}>
          <UitkCardContentSection>
            <SummarizedContent {...contentProps} />
            {!hasFullCardLink && link}
          </UitkCardContentSection>
        </UitkLayoutGridItem>
      </UitkLayoutGrid>
      {hasFullCardLink && link}
    </UitkCard>
  );
};

export const SummarizedHotelCard = observer(SummarizedHotelCardComponent);

SummarizedHotelCard.displayName = "SummarizedHotelCard";
